import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Restaurant,
  Timeline,
  Warning
} from '@mui/icons-material';
import { get, post, put, del } from '../../config/axios';

const LowFodmapTestingFoods = () => {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    testingWeek: '',
    testPortions: [
      { size: '', weight: '', description: '' },
      { size: '', weight: '', description: '' },
      { size: '', weight: '', description: '' }
    ],
    symptomsToWatch: ['bloating', 'gas', 'abdominal_pain']
  });

  const categories = [
    'fructose',
    'lactose', 
    'mannitol',
    'sorbitol',
    'fructan_garlic',
    'fructan_grain',
    'fructan_vegetable_fruits',
    'galactans'
  ];

  const testingWeeks = Array.from({ length: 14 }, (_, i) => i + 1);

  useEffect(() => {
    fetchTestingFoods();
  }, []);

  const fetchTestingFoods = async () => {
    try {
      setLoading(true);
      const response = await get('/api/dashboard/low-fodmap/testing-foods');
      if (response.success) {
        setFoods(response.data);
      }
    } catch (error) {
      console.error('Error fetching testing foods:', error);
      setError('Failed to load testing foods');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (food = null) => {
    if (food) {
      setEditingFood(food);
      setFormData({
        name: food.name,
        category: food.category,
        subcategory: food.subcategory || '',
        testingWeek: food.testingWeek,
        testPortions: food.testPortions,
        symptomsToWatch: food.symptomsToWatch
      });
    } else {
      setEditingFood(null);
      setFormData({
        name: '',
        category: '',
        subcategory: '',
        testingWeek: '',
        testPortions: [
          { size: '', weight: '', description: '' },
          { size: '', weight: '', description: '' },
          { size: '', weight: '', description: '' }
        ],
        symptomsToWatch: ['bloating', 'gas', 'abdominal_pain']
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFood(null);
    setFormData({
      name: '',
      category: '',
      subcategory: '',
      testingWeek: '',
      testPortions: [
        { size: '', weight: '', description: '' },
        { size: '', weight: '', description: '' },
        { size: '', weight: '', description: '' }
      ],
      symptomsToWatch: ['bloating', 'gas', 'abdominal_pain']
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingFood) {
        await put(`/api/dashboard/low-fodmap/testing-foods/${editingFood._id}`, formData);
      } else {
        await post('/api/dashboard/low-fodmap/testing-foods', formData);
      }
      fetchTestingFoods();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving testing food:', error);
      setError('Failed to save testing food');
    }
  };

  const handleDelete = async (foodId) => {
    if (window.confirm('Are you sure you want to delete this testing food?')) {
      try {
        await del(`/api/dashboard/low-fodmap/testing-foods/${foodId}`);
        fetchTestingFoods();
      } catch (error) {
        console.error('Error deleting testing food:', error);
        setError('Failed to delete testing food');
      }
    }
  };

  const updatePortion = (index, field, value) => {
    const newPortions = [...formData.testPortions];
    newPortions[index][field] = value;
    setFormData({ ...formData, testPortions: newPortions });
  };

  const getCategoryColor = (category) => {
    const colors = {
      fructose: 'error',
      lactose: 'info',
      mannitol: 'warning',
      sorbitol: 'secondary',
      fructan_garlic: 'primary',
      fructan_grain: 'success',
      fructan_vegetable_fruits: 'default',
      galactans: 'primary'
    };
    return colors[category] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            🍽️ Low FODMAP Testing Foods
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage FODMAP testing foods with exact portions and testing schedules
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Testing Food
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Total Testing Foods
              </Typography>
              <Typography variant="h3">{foods.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary">
                Testing Weeks
              </Typography>
              <Typography variant="h3">{new Set(foods.map(f => f.testingWeek)).size}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info">
                FODMAP Categories
              </Typography>
              <Typography variant="h3">{new Set(foods.map(f => f.category)).size}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success">
                Active Foods
              </Typography>
              <Typography variant="h3">{foods.filter(f => f.isActive).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Testing Foods Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Food Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Testing Week</TableCell>
                <TableCell>Portion Progression</TableCell>
                <TableCell>Symptoms to Watch</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {foods.map((food) => (
                <TableRow key={food._id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{food.name}</Typography>
                    {food.subcategory && (
                      <Typography variant="caption" color="text.secondary">
                        {food.subcategory}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={food.category.replace(/_/g, ' ')}
                      color={getCategoryColor(food.category)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`Week ${food.testingWeek}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      {food.testPortions.map((portion, index) => (
                        <Typography key={index} variant="caption" display="block">
                          {index + 1}. {portion.size}
                          {portion.weight && ` (${portion.weight})`}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {food.symptomsToWatch.slice(0, 3).map((symptom, index) => (
                        <Chip
                          key={index}
                          label={symptom}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                      {food.symptomsToWatch.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{food.symptomsToWatch.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="info">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(food)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(food._id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFood ? 'Edit Testing Food' : 'Add New Testing Food'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Food Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subcategory"
                value={formData.subcategory}
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                placeholder="e.g., garlic, grain, vegetables"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Testing Week</InputLabel>
                <Select
                  value={formData.testingWeek}
                  onChange={(e) => setFormData({ ...formData, testingWeek: e.target.value })}
                  label="Testing Week"
                >
                  {testingWeeks.map((week) => (
                    <MenuItem key={week} value={week}>
                      Week {week}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Portion Progression */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Portion Progression (3 Steps)
          </Typography>
          {formData.testPortions.map((portion, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Step {index + 1}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Portion Size"
                    value={portion.size}
                    onChange={(e) => updatePortion(index, 'size', e.target.value)}
                    placeholder="e.g., 1/4 clove, 1 slice"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Weight/Measurement"
                    value={portion.weight}
                    onChange={(e) => updatePortion(index, 'weight', e.target.value)}
                    placeholder="e.g., 26g, 60ml"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={portion.description}
                    onChange={(e) => updatePortion(index, 'description', e.target.value)}
                    placeholder="e.g., Start with 1/4 clove"
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingFood ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default LowFodmapTestingFoods;
